import { DialogActions, Stack } from '@mui/material';
import React, { useContext, useRef, useState } from 'react';  
import { spaces } from '../../../../assets/styles/themes/tokens';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../../state/AIAssistantContextProvider';
import { useTranslation } from 'react-i18next';
import config from 'config';
import PromptInput from './PromptInput/PromptInput';
import { useSelector } from 'react-redux';
import { getCurrentConversation, getTemporaryAttachments } from '../../../../reducers/aiAssistant.selectors';
import { AsyncActionStatus } from '../../../../actions/common.actionTypes';
import StopButton from './PromptInput/StopButton';
import SendButton from './PromptInput/SendButton';
import AttachmentsButton from './PromptInput/AttachmentsButton';
import useAction from '../../../../utils/useAction';
import {
  clearTemporaryAttachments as clearTemporaryAttachmentsAction,
  getPromptsRemainingLimit as getPromptsRemainingLimitAction,
} from '../../../../actions/aiAssistant.actions';
import useScreenSize from '../../../../utils/useScreenSize';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import useIsPodcastConversation from '../Chat/useIsPodcastConversation';

export const AI_USER_PROMPT_CHAR_LIMIT = config.ai.readerChat.maxUserPromptLength;
export const DEFAULT_DIALOG_FOOTER_HEIGHT = 200;
export const DEFAULT_PAGE_FOOTER_HEIGHT = 184;
const AIAssistantFooter = ({ caption }: { caption?: React.ReactNode }) => {
  const { t } = useTranslation();
  const {
    dialogFooterRef,
    isUserPromptLimitReached,
    isPromptLimitReached,
    showPromptLimitAlert,
    processNewConversation,
    stopResponse,
    continueConversation,
    displayMode,
    isPodcastV1UxEnabled
  } = useContext(aiAssistantContext);
  const { isTextEditorOpen } = useContext(aiAssistantEditorContext);
  const isPodcastConversation = useIsPodcastConversation();
  // Podcast conversations take no attachments.
  const showAttachmentsButton = !(isPodcastV1UxEnabled && isPodcastConversation);
  const isDialogMode = displayMode === AIAssistantDisplayMode.DIALOG;
  const temporaryAttachments = useSelector(getTemporaryAttachments);
  const clearTemporaryAttachments = useAction(clearTemporaryAttachmentsAction);
  const currentConversation = useSelector(getCurrentConversation);
  const isLoadingResponse = currentConversation?.status === AsyncActionStatus.LOADING;
  const [userPrompt, setUserPrompt] = useState('');
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isXsOrSmScreen = isXsScreen || isSmScreen;
  const formRef = useRef<HTMLFormElement>(null);
  const isSubmissionDisabled = isUserPromptLimitReached || isPromptLimitReached || userPrompt.trim().length === 0;

  const handleUserPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (value.length > AI_USER_PROMPT_CHAR_LIMIT) {
      value = value.slice(0, AI_USER_PROMPT_CHAR_LIMIT);
    }
    setUserPrompt(value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isLoadingResponse || isSubmissionDisabled) {
      showPromptLimitAlert();
      return;
    }

    const conversationData = {
      message: userPrompt,
      ...(
        temporaryAttachments.length > 0 ? 
          { messageContext: { attachments: temporaryAttachments } } 
          : {}
      )
    };

    if (currentConversation) {
      continueConversation(conversationData);
    } else {
      processNewConversation(conversationData);
    }
    
    setUserPrompt('');
    clearTemporaryAttachments();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  const handleStopButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    stopResponse();
  };

  const fetchPromptsRemainingLimit = useAction(getPromptsRemainingLimitAction);
  const handleDisabledSendClick = () => {
    if (isSubmissionDisabled && isPromptLimitReached) {
      fetchPromptsRemainingLimit();
      showPromptLimitAlert();
    }
  };

  const rightItems = isLoadingResponse ?
    <StopButton onStopResponse={handleStopButtonClick} />
    : (
      <span onClick={handleDisabledSendClick}>
        <SendButton disabled={isSubmissionDisabled} />
      </span>
    );

  return (
    <DialogActions
      ref={dialogFooterRef}
      sx={{
        padding: isDialogMode || isXsOrSmScreen || isTextEditorOpen ? spaces.md : spaces.xxl,
        minHeight: isDialogMode ? DEFAULT_DIALOG_FOOTER_HEIGHT : DEFAULT_PAGE_FOOTER_HEIGHT,
        maxWidth: '1440px',
        margin: '0 auto',
        width: '100%'
      }}
    >
      <Stack gap={spaces.md} sx={{ width: '100%' }}>
        <form ref={formRef} onSubmit={handleSubmit}>
          <PromptInput
            onChange={handleUserPromptChange}
            onKeyDown={handleKeyDown}
            promptValue={userPrompt}
            maxPromptLength={AI_USER_PROMPT_CHAR_LIMIT}
            placeholder={t('Enter prompt')}
            rightItems={rightItems}
            leftItems={showAttachmentsButton ? <AttachmentsButton /> : null}
          />
        </form>
        {caption && caption}
      </Stack>
    </DialogActions>
  );
};

export default AIAssistantFooter;
